import { useCallback, useEffect, useRef, useState } from "react";
import "../incident.css";
import { IIncident, IncidentStatus, REPORTER_LABEL, STATUS_META, typeIcon, typeLabel } from "../models/IIncident";
import { getRestaurantIncidents, incidentError, setIncidentStatus } from "../services/incidentService";
import { StatusBadge, formatDateTime } from "./IncidentBadges";

interface Props {
  restaurantId: string;
  onClose: () => void;
  /** Onay/ret sonrası bekleyen sayısı değişir; Dashboard'daki rozet güncellensin */
  onPendingChange?: (pending: number) => void;
}

const PAGE_SIZE = 20;
const FILTERS: (IncidentStatus | "")[] = ["PendingConfirmation", "", "Confirmed", "Rejected", "Disputed"];

export default function RestaurantIncidentsModal({ restaurantId, onClose, onPendingChange }: Props) {
  const [status, setStatus] = useState<IncidentStatus | "">("PendingConfirmation");
  const [items, setItems] = useState<IIncident[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(0);
  const [poolMember, setPoolMember] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Satır içi callback her render'da değişir; load'u tetiklemesin diye ref'te tutuluyor.
  const pendingCb = useRef(onPendingChange);
  pendingCb.current = onPendingChange;

  const load = useCallback((p: number) => {
    setLoading(true);
    setError(null);
    getRestaurantIncidents(restaurantId, status || undefined, p, PAGE_SIZE)
      .then((res) => {
        setItems(res.data.items);
        setTotal(res.data.totalCount);
        setPending(res.data.pendingCount);
        setPoolMember(res.data.poolMember);
        setPage(p);
        pendingCb.current?.(res.data.pendingCount);
      })
      .catch((e) => setError(incidentError(e, "Bildirimler yüklenemedi.")))
      .finally(() => setLoading(false));
  }, [restaurantId, status]);

  useEffect(() => { load(0); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const decide = async (incident: IIncident, next: "Confirmed" | "Rejected") => {
    setBusyId(incident.id);
    setError(null);
    try {
      await setIncidentStatus(incident.id, next);
      load(page);
    } catch (e) {
      setError(incidentError(e, "İşlem yapılamadı."));
      load(page);
    } finally {
      setBusyId(null);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="inc-backdrop" onClick={onClose}>
      <div className="inc-modal inc-modal--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="inc-modal__head">
          <div className="inc-modal__icon" style={{ background: "#fef2f2", color: "#dc2626" }}>
            <i className="bi bi-flag-fill"></i>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="inc-modal__title">Müşteri bildirimleri</div>
            <div className="inc-modal__sub">
              Personel ve kuryelerin açtığı bildirimler onayınızdan sonra müşteri geçmişine yansır.
            </div>
          </div>
          <button className="inc-close" onClick={onClose} aria-label="Kapat"><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="inc-modal__body">
          {poolMember === false && (
            <div className="inc-alert inc-alert--info">
              <i className="bi bi-share"></i>
              <div>Restoranınız paylaşım havuzunda değil; diğer restoranların bildirimlerini göremezsiniz, sizinkiler de paylaşılmaz.</div>
            </div>
          )}

          <div className="inc-toolbar" style={{ marginBottom: 0 }}>
            {FILTERS.map((f) => (
              <button key={f || "all"} className={`inc-chip${status === f ? " inc-chip--active" : ""}`} onClick={() => setStatus(f)}>
                {f ? STATUS_META[f].label : "Tümü"}
                {f === "PendingConfirmation" && pending > 0 && <span className="inc-tab__count">{pending}</span>}
              </button>
            ))}
          </div>

          {error && <div className="inc-alert inc-alert--error"><i className="bi bi-exclamation-circle-fill"></i><div>{error}</div></div>}

          {loading ? (
            <div style={{ textAlign: "center", padding: 24 }}><span className="spinner-border spinner-border-sm" style={{ color: "#dc2626" }} role="status" /></div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: ".86rem" }}>
              <i className="bi bi-inbox" style={{ fontSize: "1.6rem", display: "block", marginBottom: 6 }}></i>
              {status === "PendingConfirmation" ? "Onay bekleyen bildirim yok." : "Bildirim yok."}
            </div>
          ) : (
            <div className="inc-list">
              {items.map((i) => (
                <div key={i.id} className="inc-item">
                  <div className="inc-item__top">
                    <i className={`bi ${typeIcon(i.type)}`} style={{ color: "#dc2626" }}></i>
                    <span className="inc-item__title">{typeLabel(i.type)}</span>
                    <StatusBadge status={i.status} />
                    {i.orderCode && <span style={{ marginLeft: "auto", fontSize: ".74rem", fontWeight: 700, color: "#6b7280" }}>#{i.orderCode}</span>}
                  </div>
                  <div className="inc-item__desc">{i.description}</div>
                  <div className="inc-item__meta">
                    <span><i className="bi bi-person me-1"></i>{REPORTER_LABEL[i.createdByKind] ?? i.createdByKind}</span>
                    <span><i className="bi bi-clock me-1"></i>{formatDateTime(i.createdAtUtc)}</span>
                    {i.hasEvidence && <span><i className="bi bi-image me-1"></i>Görsel var</span>}
                  </div>
                  {i.status === "PendingConfirmation" && (
                    <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
                      <button className="inc-btn inc-btn--sm" onClick={() => decide(i, "Rejected")} disabled={busyId === i.id}>
                        <i className="bi bi-x-lg"></i> Reddet
                      </button>
                      <button className="inc-btn inc-btn--sm inc-btn--success" onClick={() => decide(i, "Confirmed")} disabled={busyId === i.id}>
                        {busyId === i.id ? <span className="spinner-border spinner-border-sm" role="status" /> : <><i className="bi bi-check-lg"></i> Onayla</>}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {pageCount > 1 && (
          <div className="inc-modal__foot" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <button className="inc-btn inc-btn--sm" disabled={page === 0 || loading} onClick={() => load(page - 1)}><i className="bi bi-chevron-left"></i></button>
            <span style={{ fontSize: ".78rem", color: "#6b7280" }}>{page + 1} / {pageCount}</span>
            <button className="inc-btn inc-btn--sm" disabled={page + 1 >= pageCount || loading} onClick={() => load(page + 1)}><i className="bi bi-chevron-right"></i></button>
          </div>
        )}
      </div>
    </div>
  );
}
